import clsx from 'clsx';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useTheme } from '@/app/ThemeProvider';
import { Button, Icon, IconButton, TextField } from '@/components/ui';
import { useAuth } from '../AuthProvider';
import { OAuthProvider, looksLikeEmail } from '../api';
import type { AuthErrorType } from '../errors';

import styles from './SignInPage.module.css';

/**
 * Sign-in only. Account creation happens elsewhere (the main ela app); this
 * viewer never creates accounts — the same contract as the Flutter screen.
 */
export function SignInPage() {
  const { status, busy, error, clearError, signInWithPassword, signInWithProvider } = useAuth();
  const { resolved, toggle } = useTheme();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);

  const oauthFailed = searchParams.get('oauth') === 'failed';

  const emailError = useMemo(() => {
    if (!touched) return null;
    if (email.trim() === '') return 'Email is required';
    return looksLikeEmail(email) ? null : 'Enter a valid email address';
  }, [email, touched]);

  const passwordError = touched && password === '' ? 'Password is required' : null;

  // A typed correction should clear the server's complaint about the old value.
  useEffect(() => {
    if (error) clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    if (email.trim() === '' || !looksLikeEmail(email) || password === '') return;
    void signInWithPassword(email, password);
  };

  return (
    <div className={styles.page}>
      <aside className={styles.aside}>
        <div className={styles.brand}>
          <img className={styles.mark} src="/ela-logo.svg" alt="ELA Lab" />
          <span className={styles.wordmark}>
            ela <span className={styles.wordmarkMuted}>Lab</span>
          </span>
        </div>

        <svg className={styles.trace} viewBox="0 0 1200 180" preserveAspectRatio="none" aria-hidden="true">
          <path
            d="M0 90 L40 90 L52 40 L64 140 L76 70 L88 110 L110 90 L150 90 L164 30 L176 150 L188 62 L200 118 L230 90 L280 90 L292 52 L304 128 L316 74 L328 104 L360 90 L410 90 L424 34 L436 146 L448 66 L460 116 L492 90 L540 90 L552 58 L564 124 L576 78 L588 100 L620 90 L680 90 L694 42 L706 138 L718 70 L730 110 L760 90 L820 90 L832 56 L844 126 L856 76 L868 104 L900 90 L960 90 L974 38 L986 142 L998 68 L1010 112 L1040 90 L1200 90"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>

        <div className={styles.pitch}>
          <h1 className={styles.pitchTitle}>Your EEG recordings, decoded from signal to insight.</h1>
          <p className={styles.pitchBody}>
           ELA Lab provides a deeper view into EEG recodings captured witht the ela headband - including 
           session history, brainwave patterrns, and raw signal data.
          </p>

          <ul className={styles.points}>
            <li className={styles.point}>
              <span className={styles.pointIcon}>
                <Icon name="list" size={15} />
              </span>
              All recorded EEG sessions organized in one timeline
            </li>
            <li className={styles.point}>
              <span className={styles.pointIcon}>
                <Icon name="activity" size={15} />
              </span>
              High-resolution EEG waveforms decoded directly in your browser
            </li>
            <li className={styles.point}>
              <span className={styles.pointIcon}>
                <Icon name="download" size={15} />
              </span>
              Export processed samples or original raw data files
            </li>
          </ul>
        </div>

        <div className={styles.asideFooter}>ELA Innovations · EEG Research platform</div>
      </aside>

      <section className={styles.formPane}>
        <IconButton
          icon={resolved === 'dark' ? 'sun' : 'moon'}
          label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          className={styles.themeToggle}
          onClick={toggle}
        />

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <div className={styles.formHead}>
            <h2 className={styles.formTitle}>Welcome back to ELA Lab</h2>
            <p className={styles.formSubtitle}>
              Sign in with your ela account to access your recorded EEG data.
            </p>
          </div>

          {error && error.type !== 'cancelled' ? (
            <div className={clsx(styles.alert, alertClass(error.type))} role="alert">
              <span className={styles.alertIcon}>
                <Icon name={alertIcon(error.type)} size={16} />
              </span>
              <span>{error.message}</span>
            </div>
          ) : oauthFailed ? (
            <div className={clsx(styles.alert, styles.alertAuth)} role="alert">
              <span className={styles.alertIcon}>
                <Icon name="lock" size={16} />
              </span>
              <span>That sign-in attempt did not complete. Please try again.</span>
            </div>
          ) : null}

          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            disabled={busy}
            error={emailError}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setTouched(true)}
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            disabled={busy}
            error={passwordError}
            onChange={(event) => setPassword(event.target.value)}
            trailing={
              <IconButton
                icon={showPassword ? 'eye-off' : 'eye'}
                size="sm"
                label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((current) => !current)}
                tabIndex={-1}
              />
            }
          />

          <Button type="submit" variant="primary" size="lg" busy={busy} fullWidth icon="logout">
            Sign in
          </Button>

          <div className={styles.divider}>or</div>

          <div className={styles.providers}>
            <Button
              size="lg"
              fullWidth
              disabled={busy}
              className={styles.providerButton}
              onClick={() => void signInWithProvider(OAuthProvider.Google)}
            >
              <img className={styles.providerIcon} src="/google-icon-logo.svg" alt="" aria-hidden="true" />
              Continue with Google
            </Button>
            <Button
              size="lg"
              fullWidth
              disabled={busy}
              className={styles.providerButton}
              onClick={() => void signInWithProvider(OAuthProvider.Apple)}
            >
              <img className={styles.providerIcon} src="/apple-logo.svg" alt="" aria-hidden="true" />
              Continue with Apple
            </Button>
          </div>

          <p className={styles.note}>
            ELA Lab does not create accounts. Register in the ela app, then sign in here.
          </p>
        </form>
      </section>
    </div>
  );
}

function alertClass(type: AuthErrorType): string | undefined {
  switch (type) {
    case 'network':
      return styles.alertNetwork;
    case 'authentication':
    case 'authorization':
    case 'server':
      return styles.alertAuth;
    case 'validation':
      return styles.alertValidation;
    default:
      return styles.alertUnknown;
  }
}

function alertIcon(type: AuthErrorType) {
  switch (type) {
    case 'network':
      return 'cloud-off' as const;
    case 'authentication':
    case 'authorization':
      return 'lock' as const;
    case 'server':
      return 'cloud-off' as const;
    default:
      return 'alert' as const;
  }
}
